import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/i18n";
import { ShopPlanCard } from "@/components/ShopPlanCard";
import PAGES from "@/seo/pages.json";

export default function Pricing() {
  const { user } = useAuth();
  const { t, tErr, locale, href, lang } = useLang();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(false);
  const total = 5 + 4.5 * (qty - 1);
  const usd = (v) => (lang === "en" ? `US$${v.toFixed(2)}` : new Intl.NumberFormat(locale, { style: "currency", currency: "USD" }).format(v));

  useEffect(() => { if (params.get("cancelado")) toast(t("pricing.cancelled")); }, [params, t]);

  const buy = async () => {
    if (!user) { navigate(`/entrar?next=${href("pricing")}`); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { plan: "pack", quantity: qty, origin_url: window.location.origin, lang });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(tErr(errMsg(e))); setLoading(false); }
  };

  return (
    <div className="grid-paper min-h-[calc(100vh-148px)]" data-testid="pricing-page">
      <div className="mx-auto max-w-6xl px-4 py-20 sm:px-6 lg:px-8">
        <p className="font-mono text-xs uppercase tracking-wider text-slate-400">{t("pricing.eyebrow")}</p>
        <h1 className="mt-3 text-4xl font-bold tracking-tight text-slate-900 sm:text-5xl">{PAGES.pricing[lang].h1}</h1>
        <p className="mt-4 max-w-xl text-slate-600">{t("pricing.sub")}</p>
        <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <div className="rounded-2xl border border-slate-200 bg-white p-8">
            <p className="font-mono text-xs uppercase tracking-wider text-slate-400">{t("pricing.free")}</p>
            <p className="mt-4 font-display text-5xl font-bold text-slate-900">{usd(0)}</p>
            <p className="mt-1 text-sm text-slate-500">{t("pricing.toStart")}</p>
            <ul className="mt-8 space-y-3">{t("pricing.freeList").map((f) => <li key={f} className="flex gap-2 text-sm text-slate-700"><Check className="h-4 w-4 text-emerald-500" />{f}</li>)}</ul>
          </div>
          <div className="relative rounded-2xl border-2 border-slate-900 bg-white p-8 shadow-xl">
            <span className="absolute -top-3 left-8 rounded-full bg-[#E40046] px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-white">{t("pricing.pack")}</span>
            <p className="font-mono text-xs uppercase tracking-wider text-slate-400">{t("pricing.credits10", { n: 10 * qty })}</p>
            <p className="mt-4 font-display text-5xl font-bold text-slate-900" data-testid="pricing-total">{usd(total)}</p>
            <p className="mt-1 text-sm text-slate-500" data-testid="pricing-per-file">{t("pricing.perFile", { n: 10 * qty, per: usd(total / (10 * qty)) })}</p>
            {qty > 1 ? <p className="mt-2 inline-block rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700" data-testid="pricing-savings">{t("pricing.save", { v: usd(5 * qty - total) })}</p>
              : <p className="mt-2 text-xs text-slate-500" data-testid="pricing-extra-note">{t("pricing.extraNote", { price: usd(4.5) })}</p>}
            <div className="mt-8 flex items-center justify-between rounded-xl border border-slate-200 p-2">
              <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="grid h-10 w-10 place-items-center rounded-lg hover:bg-slate-100" data-testid="pricing-qty-minus"><Minus className="h-4 w-4" /></button>
              <span className="font-mono text-sm font-semibold" data-testid="pricing-qty">{t(qty === 1 ? "pricing.pack1" : "pricing.packs", { n: qty })}</span>
              <button onClick={() => setQty((q) => Math.min(20, q + 1))} className="grid h-10 w-10 place-items-center rounded-lg hover:bg-slate-100" data-testid="pricing-qty-plus"><Plus className="h-4 w-4" /></button>
            </div>
            <Button onClick={buy} disabled={loading} className="mt-6 h-12 w-full rounded-full bg-slate-900 text-base hover:bg-slate-800" data-testid="pricing-buy-button">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}{t("pricing.buyBtn", { n: 10 * qty })}
            </Button>
            <p className="mt-3 text-center text-xs text-slate-400">{t("pricing.secure")}</p>
          </div>
          <ShopPlanCard usd={usd} />
        </div>
      </div>
    </div>
  );
}
